<script lang="ts" setup>
import { MdEditor, type ToolbarNames } from 'md-editor-v3';
import 'md-editor-v3/lib/style.css';
import { sanitizeMarkdownHtml } from '~/utils/sanitize-markdown-html';
import { uploadImage } from '~/composables/image-upload';
import {
  SystemAnnouncementCategory,
  SystemAnnouncementCategoryList,
  SystemAnnouncementStatus,
} from '~/models/system-announcement';
import { systemAnnouncementService } from '~/services/system-announcement.service';
import { useSnackbarStore } from '~/store/snackbar';

const route = useRoute();
const { t } = useI18n();
const snackbar = useSnackbarStore();

const uid = computed(() => (route.params.uid as string | undefined) || null);
const isEdit = computed(() => !!uid.value);

useHead({
  title: t(isEdit.value ? 'pages.announcements.compose.edit_title' : 'pages.announcements.compose.title'),
});

const TITLE_MAX = 120;

const category = ref<SystemAnnouncementCategory>(SystemAnnouncementCategory.RELEASE_NOTE);
const status = ref<SystemAnnouncementStatus>(SystemAnnouncementStatus.ACTIVE);
const title = ref('');
const body = ref('');
const showPreview = ref(true);
const saving = ref(false);
const createdAt = ref<string | null>(null);

if (isEdit.value && uid.value) {
  const { result } = await systemAnnouncementService.show(uid.value);
  if (result?.data) {
    category.value = result.data.category;
    status.value = result.data.status;
    title.value = result.data.title;
    body.value = result.data.body;
    createdAt.value = result.data.created_at;
  }
}

// When editing an already-active announcement, the status is locked to active
// (no revert to draft; that would suppress the already-scheduled email).
const isEditingActive = computed(() => isEdit.value && status.value === SystemAnnouncementStatus.ACTIVE);
// Show the "Save draft" button on new items and when editing a draft.
const showDraftButton = computed(() => !isEditingActive.value);

const breadcrumbs = computed(() => [
  { label: t('side_bar.announcements'), to: '/announcements' },
  { label: t(isEdit.value ? 'common.breadcrumb.edit' : 'common.breadcrumb.new') },
]);

const pageTitle = computed(() =>
  t(isEdit.value ? 'pages.announcements.compose.edit_title' : 'pages.announcements.compose.title'),
);

const canSubmit = computed(() => title.value.trim().length > 0 && body.value.trim().length > 0);

const onSubmit = async (submitStatus: SystemAnnouncementStatus) => {
  if (!canSubmit.value || saving.value) return;
  saving.value = true;
  const payload = {
    category: category.value,
    status: submitStatus,
    title: title.value.trim(),
    body: body.value,
  };
  const op = isEdit.value
    ? systemAnnouncementService.update(uid.value!, payload)
    : systemAnnouncementService.create(payload);
  const { error } = await op;
  saving.value = false;
  if (!error) {
    const wasDraftSave = submitStatus === SystemAnnouncementStatus.DRAFT;
    snackbar.showMessage({
      content: t(
        isEdit.value && !wasDraftSave
          ? 'announcements.updated'
          : wasDraftSave
            ? 'announcements.draft_saved'
            : 'announcements.published',
      ),
      color: 'success',
    });
    navigateTo('/announcements');
  }
};

const editorToolbars: ToolbarNames[] = [
  'title',
  'bold',
  'underline',
  'italic',
  'strikeThrough',
  '-',
  'unorderedList',
  'orderedList',
  '-',
  'link',
  'image',
  'table',
  'codeRow',
  'code',
  'quote',
];

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to read file'));
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const onUploadImg = async (files: File[], callback: (urls: string[]) => void) => {
  const urls = await Promise.all(
    files.map(async file => {
      try {
        return await uploadImage(await readFileAsDataUrl(file));
      } catch {
        return '';
      }
    }),
  );
  callback(urls.filter(url => url !== ''));
};
</script>

<template>
  <page-content
    :breadcrumbs="breadcrumbs"
    :title="pageTitle"
  >
    <template #actions>
      <m-btn
        variant="text"
        :prepend-icon="showPreview ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
        @click="showPreview = !showPreview"
      >
        {{ showPreview ? t('announcements.hide_preview') : t('announcements.show_preview') }}
      </m-btn>
      <m-btn
        v-if="showDraftButton"
        variant="outlined"
        prepend-icon="mdi-content-save-outline"
        :disabled="!canSubmit"
        :loading="saving"
        @click="onSubmit(SystemAnnouncementStatus.DRAFT)"
      >
        {{ t('announcements.save_draft') }}
      </m-btn>
      <m-btn
        color="primary"
        prepend-icon="mdi-send-outline"
        :disabled="!canSubmit"
        :loading="saving"
        @click="onSubmit(SystemAnnouncementStatus.ACTIVE)"
      >
        {{ t(isEditingActive ? 'announcements.save_changes' : 'announcements.publish') }}
      </m-btn>
    </template>

    <div class="compose-page">
      <div
        class="compose-grid"
        :class="{ 'has-preview': showPreview }"
      >
        <m-card
          class="pa-6"
          elevation="0"
        >
          <h3 class="text-subtitle-1 font-weight-bold mb-4">{{ t('announcements.section_details') }}</h3>

          <div class="mb-4">
            <label class="d-block text-body-2 font-weight-medium mb-1 field-label">{{
              t('models.system_announcement_attrs.category')
            }}</label>
            <m-select
              v-model="category"
              :items="
                SystemAnnouncementCategoryList.map(c => ({
                  value: c,
                  title: t(`enums.system_announcement_category.${c}`),
                }))
              "
              item-title="title"
              item-value="value"
            />
          </div>

          <div class="mb-4">
            <label class="d-block text-body-2 font-weight-medium mb-1 field-label">{{
              t('models.system_announcement_attrs.title')
            }}</label>
            <m-text-field
              v-model="title"
              :maxlength="TITLE_MAX"
              :placeholder="t('announcements.title_placeholder')"
            />
            <div class="text-right text-caption mt-1 text-medium-emphasis">{{ title.length }}/{{ TITLE_MAX }}</div>
          </div>

          <div class="mb-4">
            <label class="d-block text-body-2 font-weight-medium mb-1 field-label">{{
              t('models.system_announcement_attrs.body')
            }}</label>
            <MdEditor
              v-model="body"
              language="en-US"
              :toolbars="editorToolbars"
              :preview="false"
              :sanitize="sanitizeMarkdownHtml"
              :placeholder="t('announcements.body_placeholder')"
              style="height: 420px"
              no-katex
              no-mermaid
              @on-upload-img="onUploadImg"
            />
          </div>
        </m-card>

        <m-card
          v-if="showPreview"
          class="pa-6"
          elevation="0"
        >
          <h3 class="text-subtitle-1 font-weight-bold mb-4">{{ t('announcements.preview') }}</h3>
          <div class="pa-5 rounded-lg border">
            <system-announcement-display
              :category="category"
              :title="title"
              :body="body"
              :created-at="createdAt"
              is-new
              preview
            />
          </div>
        </m-card>
      </div>
    </div>
  </page-content>
</template>

<style lang="scss" scoped>
.compose-page {
  max-width: 1400px;
}

.compose-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  &.has-preview {
    grid-template-columns: 1fr 1fr;
  }
}

.field-label {
  color: rgb(var(--v-theme-dark-900));
}
</style>
